import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import App from './App';
import { getTheme, setTheme } from './lib/theme';
import './index.css';

/**
 * TanStack Query owns server state; Zustand will own client state (player,
 * auth, chat) when those arrive in Phase 2 and 4. Keeping that split clean from
 * the start is what stops the player store from slowly accumulating a cache.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A feed or a chart is not stale the instant it is fetched, and refetching
      // on every window focus is how you turn a background tab into traffic.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Retrying a 401 or a 422 cannot succeed and delays the error the user
      // needs to see. Only server-side and network failures are worth a retry.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status ?? 0;

        return status >= 500 && failureCount < 2;
      },
    },
  },
});

const root = document.getElementById('root');

if (!root) {
  throw new Error('No #root element - index.html and main.tsx disagree.');
}

/*
 * Re-apply the stored theme once, at boot.
 *
 * The inline script in `index.html` already set the attribute - that is what
 * prevents the flash - but it deliberately does nothing else. This second pass
 * costs one attribute write and picks up the part that needs the stylesheet to
 * have loaded: the `theme-color` meta tag, which is a literal the CSS cannot
 * reach and which otherwise leaves a near-black browser chrome sitting above a
 * light interface on a phone.
 */
setTheme(getTheme());

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
